import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { loadTranslations } from './i18n';
import { useAvailableLanguages } from '@/components/core/language-selector/hooks/use-language';

/**
 * Type definition for the Language Context.
 * Provides language-related state and functionality throughout the application.
 *
 * @interface LanguageContextType
 */
interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string, isUserAction?: boolean) => Promise<void>;
  isLoading: boolean;
  availableLanguages: any[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Props for the LanguageProvider component.
 *
 * @interface LanguageProviderProps
 */
interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: string;
}

/**
 * Cache to store loaded translation modules for each language
 * Structure: { [language: string]: Set<string> }
 */
const translationCache: Record<string, Set<string>> = {};

/**
 * Hardcoded module name - all translations are in a single module
 */
const TRANSLATION_MODULE = 'all_keys';

/**
 * Retrieves the initial language from localStorage or falls back to default.
 * This ensures language persistence across page reloads.
 */
const getInitialLanguage = (defaultLanguage: string): string => {
  if (typeof window !== 'undefined') {
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage) {
      return storedLanguage;
    }
  }
  return defaultLanguage;
};

export function LanguageProvider({
  children,
  defaultLanguage = 'en-US',
}: Readonly<LanguageProviderProps>) {
  // Initialize with stored language from localStorage if available, otherwise use default
  const [currentLanguage, setCurrentLanguage] = useState<string>(() =>
    getInitialLanguage(defaultLanguage)
  );

  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();
  const { data: languages = [], isLoading: isLanguagesLoading } = useAvailableLanguages();
  const isInitialized = useRef(false);
  const hasCheckedDefaultLanguage = useRef(false);
  const lastApiDefaultLanguage = useRef<string | null>(null);

  useEffect(() => {
    setIsLoading(isLanguagesLoading);
  }, [isLanguagesLoading]);

  /**
   * Checks if the translation module is cached for a language
   */
  const isModuleCached = useCallback((language: string): boolean => {
    return translationCache[language]?.has(TRANSLATION_MODULE) ?? false;
  }, []);

  /**
   * Adds the loaded module to the cache
   */
  const cacheModule = useCallback((language: string): void => {
    if (!translationCache[language]) {
      translationCache[language] = new Set();
    }
    translationCache[language].add(TRANSLATION_MODULE);
  }, []);

  /**
   * Loads translation module for a given language.
   *
   * @param {string} language - Language code to load translations for
   * @returns {Promise<void>} Resolves when module is loaded
   */
  const loadLanguageModules = useCallback(
    async (language: string) => {
      if (isModuleCached(language)) {
        return;
      }

      try {
        await loadTranslations(language, TRANSLATION_MODULE);
        cacheModule(language);
      } catch (err) {
        console.error(`Failed to load translations for module ${TRANSLATION_MODULE}:`, err);
      }
    },
    [isModuleCached, cacheModule]
  );

  /**
   * Changes the application's active language.
   * - Persists the language choice to localStorage
   * - Loads required translation modules
   * - Updates i18n instance and context state
   *
   * @param {string} language - The language code to switch to
   * @param {boolean} isUserAction - Whether this is triggered by user action (default: true)
   * @returns {Promise<void>} Resolves when language change is complete
   */
  const setLanguage = useCallback(
    async (language: string, isUserAction = true): Promise<void> => {
      setIsLoading(true);
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem('language', language);
          // Mark if this was an explicit user selection
          if (isUserAction) {
            localStorage.setItem('language_user_selected', 'true');
          }
        }

        await loadLanguageModules(language);
        i18n.changeLanguage(language);
        setCurrentLanguage(language);
        isInitialized.current = true; // Mark as initialized after language change
      } catch (error) {
        console.error('Failed to change language:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [loadLanguageModules, i18n]
  );

  /**
   * Effect to check and apply the default language from the API.
   * This runs when languages are loaded and automatically detects when the
   * API's default language changes, applying it unless user has explicitly chosen a language.
   */
  useEffect(() => {
    if (isLanguagesLoading || languages.length === 0) {
      return;
    }

    const storedLanguage = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    const userExplicitChoice =
      typeof window !== 'undefined' ? localStorage.getItem('language_user_selected') : null;
    const apiDefaultLanguage = languages.find((lang) => lang.isDefault);

    if (!apiDefaultLanguage) {
      return;
    }

    const apiDefaultCode = apiDefaultLanguage.languageCode;

    // Detect if the API default language has changed
    const apiDefaultChanged =
      lastApiDefaultLanguage.current !== null && lastApiDefaultLanguage.current !== apiDefaultCode;

    // Update the last known API default
    lastApiDefaultLanguage.current = apiDefaultCode;

    // If user has explicitly selected a language, respect it unless API default changed
    if (userExplicitChoice === 'true' && storedLanguage && !apiDefaultChanged) {
      const storedLanguageExists = languages.some((lang) => lang.languageCode === storedLanguage);
      if (storedLanguageExists) {
        // Load translations if needed (in case i18n hasn't loaded them yet)
        if (storedLanguage !== currentLanguage || !hasCheckedDefaultLanguage.current) {
          setLanguage(storedLanguage, false);
        }
        hasCheckedDefaultLanguage.current = true;
        return;
      }
    }

    // Apply the API's default language if:
    // 1. First time checking (initial load) AND no user explicit choice
    // 2. API default has changed
    // 3. Stored language doesn't exist in available languages
    if (!hasCheckedDefaultLanguage.current || apiDefaultChanged || userExplicitChoice !== 'true') {
      if (apiDefaultCode !== currentLanguage) {
        setLanguage(apiDefaultCode, false);
      }
      hasCheckedDefaultLanguage.current = true;
    }
  }, [languages, isLanguagesLoading, currentLanguage, setLanguage]);

  /**
   * Effect hook to initialize translations when the component mounts.
   * Loads initial translation modules and sets up the language.
   * Waits for languages to be loaded to ensure we use the correct default language.
   */
  useEffect(() => {
    // Don't initialize until languages are loaded and default language check is complete
    if (isInitialized.current || isLanguagesLoading || !hasCheckedDefaultLanguage.current) {
      return;
    }

    const initializeTranslations = async () => {
      setIsLoading(true);
      try {
        await loadLanguageModules(currentLanguage);
        i18n.changeLanguage(currentLanguage);
        isInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize translations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeTranslations();
  }, [currentLanguage, loadLanguageModules, i18n, isLanguagesLoading]);

  const value = useMemo(
    () => ({
      currentLanguage,
      setLanguage,
      isLoading,
      availableLanguages: languages,
    }),
    [currentLanguage, setLanguage, isLoading, languages]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};
